import clsx from 'clsx';
import React, { useContext, useEffect, useMemo, useRef } from 'react';

import { PortalConfigContext } from 'src/context';
import { getPublicImageURL } from 'src/utils/ImageUtils';

interface StyleProps {
  logoHeight?: number;
}
interface PortalFullLogoProps extends StyleProps {
  isPreviewLogo?: boolean;
  previewFullLogoUrl?: string;
}

const PortalFullLogo = ({
  isPreviewLogo = false,
  previewFullLogoUrl,
  logoHeight = 100,
}: PortalFullLogoProps) => {
  const [isImageLoaded, setIsImageLoaded] = React.useState(false);
  const portalConfig = useContext(PortalConfigContext);
  const imageRef = useRef<HTMLImageElement>(null);

  // Derived during render rather than in an effect so the server-rendered
  // markup already reserves the logo's space instead of adding it after
  // hydration and pushing the auth card down.
  const logoPath = useMemo(() => {
    const url = isPreviewLogo
      ? previewFullLogoUrl
      : portalConfig.assets.logo.logoImageUrl;
    return url ? getPublicImageURL(url, { resize: { height: 100 } }) : '';
  }, [isPreviewLogo, previewFullLogoUrl, portalConfig]);

  // A cached image can finish loading before hydration attaches onLoad, which
  // would leave the logo faded out forever.
  useEffect(() => {
    if (imageRef.current?.complete) {
      setIsImageLoaded(true);
    }
  }, []);

  if (!logoPath) return null;
  return (
    <a href="/" className="flex w-full max-w-full justify-center no-underline">
      <div
        className={clsx(
          'rounded-s-4 flex w-full max-w-full object-contain transition justify-center',
          { 'opacity-0': !isImageLoaded },
          { 'opacity-100': isImageLoaded },
        )}
        style={{ height: logoHeight }}
      >
        <img
          ref={imageRef}
          className="max-h-full max-w-full object-contain"
          alt={portalConfig.metadata.title || portalConfig.name || 'Logo'}
          src={logoPath}
          onLoad={() => {
            setIsImageLoaded(true);
          }}
        />
      </div>
    </a>
  );
};

export default PortalFullLogo;
